import React, { useState } from 'react';
import {
  List,
  Avatar,
  Badge,
  Typography,
  Spin,
  Button,
  Popconfirm,
  Tag,
  Segmented,
  Space
} from 'antd';
import { UserOutlined, DeleteOutlined, UserAddOutlined, CheckCircleOutlined, ThunderboltOutlined, LockOutlined } from '@ant-design/icons';
import { chatService } from '../../services/chatService';

interface Conversation {
  sender_id: number;
  last_message_date: string;
  unread_count: number;
  ticket?: {
    id: number;
    status: 'unassigned' | 'in_progress' | 'closed';
    assignedAgentId: number | null;
    assignedAgentName: string | null;
    category?: string;
    resolutionNotes?: string;
    closedAt?: string;
  };
  sender: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
    customerProfile?: {
      first_name: string;
      last_name: string;
    };
  };
}

interface ConversationListProps {
  conversations: Conversation[];
  selectedUserId: number | null;
  currentUserId?: number;
  onSelectConversation: (userId: number) => void;
  onDeleteConversation?: (userId: number) => void;
  onAssignConversation?: (userId: number) => void;
  onTabChange?: (tab: string) => void;
  isLoading: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedUserId,
  currentUserId,
  onSelectConversation,
  onDeleteConversation,
  onAssignConversation,
  onTabChange,
  isLoading,
}) => {
  const [filterTab, setFilterTab] = useState<'all' | 'unassigned' | 'my' | 'closed'>('all');

  const getDisplayName = (conversation: Conversation) => {
    if (!conversation.sender) {
      return 'Utilisateur inconnu';
    }
    if (conversation.sender.customerProfile) {
      const { first_name, last_name } = conversation.sender.customerProfile;
      return `${first_name} ${last_name}`.trim() || conversation.sender.email;
    }
    return `${conversation.sender.first_name} ${conversation.sender.last_name}`.trim() || conversation.sender.email;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `Il y a ${minutes} min`;
    } else if (hours < 24) {
      return `Il y a ${hours}h`;
    } else {
      const days = Math.floor(hours / 24);
      return `Il y a ${days}j`;
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const ticketStatus = conv.ticket?.status || 'unassigned';
    // L'onglet 'closed' est géré côté API, ici toutes les conv sont clôturées
    if (filterTab === 'closed') return true;
    if (filterTab === 'unassigned') {
      return ticketStatus === 'unassigned';
    }
    if (filterTab === 'my') {
      return conv.ticket?.assignedAgentId && Number(conv.ticket.assignedAgentId) === Number(currentUserId);
    }
    return true;
  });

  const unassignedCount = conversations.filter(c => (c.ticket?.status || 'unassigned') === 'unassigned').length;
  const myCount = conversations.filter(c => c.ticket?.assignedAgentId && Number(c.ticket.assignedAgentId) === Number(currentUserId)).length;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#ffffff' }}>
      {/* FILTRES ONGLET CALL CENTER */}
      <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
        <Segmented
          block
          size="small"
          value={filterTab}
          onChange={(val) => {
            const newTab = val as string;
            setFilterTab(newTab as any);
            if (onTabChange) onTabChange(newTab);
          }}
          options={[
            { label: `Tous (${conversations.length})`, value: 'all' },
            { label: `⚡ Attente (${unassignedCount})`, value: 'unassigned' },
            { label: `👤 Mes chats (${myCount})`, value: 'my' },
            { label: <span><LockOutlined /> Clôturées</span>, value: 'closed' }
          ]}
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filteredConversations.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Typography.Text type="secondary">
              Aucune conversation dans cette vue
            </Typography.Text>
          </div>
        ) : (
          <List
            style={{ width: '100%', backgroundColor: '#fff', padding: 0 }}
            dataSource={filteredConversations}
            renderItem={(conversation) => {
              const ticket = conversation.ticket || { status: 'unassigned', assignedAgentId: null, assignedAgentName: null };
              const isSelected = selectedUserId === conversation.sender_id;
              const isUnknownUser = !conversation.sender || !conversation.sender.id;
              const isUnassigned = ticket.status === 'unassigned';
              const isMine = ticket.assignedAgentId === currentUserId;

              return (
                <List.Item
                  key={conversation.sender_id}
                  style={{
                    borderBottom: '1px solid #f0f0f0',
                    padding: 0,
                    backgroundColor: isSelected ? '#e6f7ff' : isUnknownUser ? '#f5f5f5' : isUnassigned ? '#fffbf0' : 'transparent',
                    borderLeft: isSelected ? '4px solid #1677ff' : isUnknownUser ? '4px solid #d9d9d9' : isUnassigned ? '4px solid #fa8c16' : 'none',
                    opacity: isUnknownUser ? 0.75 : 1
                  }}
                  onClick={() => !isUnknownUser && onSelectConversation(conversation.sender_id)}
                >
                  <List.Item.Meta
                    style={{ padding: '12px 14px', cursor: 'pointer' }}
                    avatar={
                      <Badge count={conversation.unread_count} offset={[-2, 2]}>
                        <Avatar
                          style={{ backgroundColor: isUnassigned ? '#d97706' : isMine ? '#0a543d' : '#2563eb' }}
                          icon={<UserOutlined />}
                        />
                      </Badge>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography.Text strong style={{ fontSize: 14 }}>
                          {getDisplayName(conversation)}
                        </Typography.Text>

                        {isUnassigned ? (
                          <Tag color="volcano" style={{ margin: 0, fontSize: 10, borderRadius: 8 }}>En attente</Tag>
                        ) : ticket.status === 'closed' ? (
                          <Tag color="green" style={{ margin: 0, fontSize: 10, borderRadius: 8 }}>Clos</Tag>
                        ) : (
                          <Tag color="blue" style={{ margin: 0, fontSize: 10, borderRadius: 8 }}>
                            {isMine ? 'Moi' : ticket.assignedAgentName || 'Agent'}
                          </Tag>
                        )}
                      </div>
                    }
                    description={
                      <div style={{ marginTop: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography.Text type="secondary" style={{ fontSize: '0.75rem' }}>
                            {formatDate(conversation.last_message_date)}
                          </Typography.Text>

                          {ticket.status === 'closed' && ticket.resolutionNotes && (
                            <Typography.Text type="secondary" style={{ fontSize: '0.7rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              📝 {ticket.resolutionNotes}
                            </Typography.Text>
                          )}

                          {/* Utilisateur inconnu (compte supprimé) → bouton Supprimer uniquement */}
                          {isUnknownUser && onDeleteConversation && (
                            <Popconfirm
                              title="Supprimer cette conversation ?"
                              description="Le compte utilisateur n'existe plus. Supprimer l'historique des messages ?"
                              onConfirm={(e) => {
                                e?.stopPropagation();
                                onDeleteConversation(conversation.sender_id);
                              }}
                              okText="Supprimer"
                              cancelText="Annuler"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                style={{ fontSize: 11, borderRadius: 6 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                Supprimer
                              </Button>
                            </Popconfirm>
                          )}

                          {/* Utilisateur connu en attente → bouton Prendre en charge */}
                          {!isUnknownUser && isUnassigned && onAssignConversation && (
                            <Button
                              type="primary"
                              size="small"
                              icon={<UserAddOutlined />}
                              style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', fontSize: 11, borderRadius: 6 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAssignConversation(conversation.sender_id);
                              }}
                            >
                              Prendre en charge
                            </Button>
                          )}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ConversationList;
